<script setup>
import BnDatePicker from '@/components/BnDatePicker.vue';
import BnMedicationWindowFields from '@/components/questionnaires/widgets/BnMedicationWindowFields.vue';
import BnToolbarBtn from '@/components/BnToolbarBtn';
import DataProvider from '@/components/DataProvider';
import {
    administrationTypeOf,
    buildSegment,
    carrierOf,
    DOSE_AMOUNT_OPTIONS,
    doseQuantityOf,
    dosesPerDay,
    isHeld,
    isPrn,
    nextOccurrences,
    rechainTaper,
    segmentChain,
    todayIn,
} from '@/common/medicationSchedule';
import {
    accountAdministrationWindow,
    blankWindow,
    enrolledSiteOf,
    HOLD_REASON_QUERY,
    holdReasonConceptOf,
    holdReasonItems as itemsForHoldReasons,
    filledTimes,
    grownTimes,
    intervalLabel as labelForInterval,
    unitName,
    isWindowComplete,
    segmentEndDay,
    segmentStartDay,
    usesDayOfWeek,
    windowFromSegment,
    windowFrequencyMismatch,
    windowPerInterval,
    windowRepeat,
    windowSegmentInput,
} from '@/common/medicationWindow';
import { addDays, format, parseISO } from 'date-fns';
import { formatClockTime, formatDate } from '@/filters/dateTimeFilters';
import { useAuthStore } from '@/stores/auth';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { computed, ref, watch } from 'vue';
import { useDisplay } from 'vuetify';

const props = defineProps({
    modelValue: {
        type: Boolean,
        default: false,
    },
    medication: {
        type: Object,
        required: true,
    },
    // Whether this medication is already on the record. An unsaved taper is still being
    // written, so its steps are handed back to be edited rather than treated as ordered.
    saved: {
        type: Boolean,
        default: false,
    },
    // The order's sig. Handed in rather than read off the carrier, which does not carry it
    // until the medication is saved — and this dialog opens before that on a new medication.
    sig: {
        type: String,
        default: '',
    },
    timeZone: {
        type: String,
        default: undefined,
    },
    // Step 1 inherits the medication's dose, so it inherits the order's frequency and
    // interval too. Steps 2+ are the taper itself and vary by design — that is what a dose
    // taper, a frequency taper, or a combination of the two is.
    orderFrequency: {
        type: Number,
        default: null,
    },
    orderPeriod: {
        type: Number,
        default: 1,
    },
    // Where this patient's medications can be given. Empty offers no choice at all, which is
    // the answer for a patient enrolled nowhere.
    sites: {
        type: Array,
        default: () => [],
    },
    orderPeriodUnit: {
        type: String,
        default: 'd',
    },
    // The user may not change this medication at all.
    readOnly: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits(['update:modelValue', 'saved']);

const authStore = useAuthStore();
const vuetifyDisplay = useDisplay();

// A step is an administration window like any other, so it offers every type one can be —
// including a hold, which is how a taper pauses without the chain being broken.
const typeOptions = [
    { value: 'scheduled', title: 'Scheduled' },
    { value: 'prn', title: 'PRN (as needed)' },
    { value: 'hold', title: 'Hold' },
];
const durationUnitOptions = [
    { value: 'd', title: 'Day(s)' },
    { value: 'wk', title: 'Week(s)' },
    { value: 'mo', title: 'Month(s)' },
];
const doseAmountOptions = DOSE_AMOUNT_OPTIONS;

// Offered, not enforced: a combobox, so a longer step is still typeable.
const stepDurationOptions = Array.from({ length: 30 }, (item, index) => String(index + 1));

const startDate = ref(null);
const steps = ref([]);
const afterFinal = ref('stop');
// Acknowledged per step, because the warning is shown per step: agreeing to one
// ascending step is not agreement to another added later.
const titrationAcknowledgedSteps = ref(new Set());
const taperNotes = ref('');

// One site for the course, not one per step. A taper is a single stretch of the same medication
// at the same place; a step that has to move is re-sited on its own segment afterwards, where the
// field lives anyway. Asking eight times for one answer is how a wrong one gets entered.
const site = ref(null);

// Where the patient is, which is what the taper is given at unless it says otherwise.
const patientSite = computed(() => enrolledSiteOf(props.sites));

// Dispensing only where the whole course is the patient's to take. A taper with one clinic-given
// step is a taper the clinic administers, and the field names the harder of the two jobs.
const siteLabel = computed(() => {
    const course = [...existingSteps.value, ...steps.value];
    return course.length && course.every((step) => step.selfAdministered === true) ? 'Dispense site' : 'Administration site';
});
const holdReasonConcepts = ref([]);

// Step indexes whose time a picker could not read. The model keeps its previous value in
// that case, so saving would write a time the step is not showing.
const stepsWithUnreadableTime = ref(new Set());

const isOpen = computed({
    get: () => props.modelValue,
    set: (value) => emit('update:modelValue', value),
});

const today = computed(() => todayIn(props.timeZone));

const carrier = computed(() => carrierOf(props.medication));

const carrierDose = computed(() => doseQuantityOf(carrier.value) ?? null);

const doseDisplay = computed(() => {
    if (carrierDose.value) return [carrierDose.value.value, carrierDose.value.unit].filter(Boolean).join(' ');
    return carrier.value?.text || 'Not recorded';
});

const medicationName = computed(() => props.medication?.medication?.CodeableConcept?.text || '');

// The sig states the dose the taper descends from; every step below states its own.
const sigLine = computed(() => props.sig || carrier.value?.timing?.code?.text || carrier.value?.text || '');

// A taper is a sequence of doses measured against the medication's own. Free text cannot be
// compared, so neither the per-step stamp nor the ascending-step check means anything —
// the taper is refused rather than built on a dose nothing can read.
const hasStructuredDose = computed(() => Number.isFinite(Number(carrierDose.value?.value)));

// A taper already on the record is not re-entered. Its steps have dates the administration
// pass has already reasoned about, so the dialog only ever appends to the end of it.
const isTaper = computed(() => administrationTypeOf(props.medication) === 'taper');

// Read off the medication, not off the steps: a final step running until cancelled carries no
// duration, so a one-step taper has nothing for a step-level test to recognise.
const existingSteps = computed(() => (props.saved && isTaper.value ? segmentChain(props.medication) : []));

const isAppend = computed(() => existingSteps.value.length > 0);

// The same steps before the medication is saved: a draft the dialog reopens for editing.
const draftSteps = computed(() => (props.saved || !isTaper.value ? [] : segmentChain(props.medication)));

const lastExisting = computed(() => existingSteps.value[existingSteps.value.length - 1] || null);

const lastExistingEnd = computed(() => segmentEndDay(lastExisting.value));

// The day after the current final step ends. A final step left running until cancelled has no
// end, so the user names the day the new step takes over and that is what closes it (D3).
const appendStart = computed(() => (lastExistingEnd.value ? format(addDays(parseISO(lastExistingEnd.value), 1), 'yyyy-MM-dd') : null));

// The taper has already run out: the day after its final step is behind us, so it is not a day
// a step can be added on (D14). The date becomes the user's to name rather than a fixed one.
const appendStartInPast = computed(() => Boolean(appendStart.value && appendStart.value < today.value));

const lastExistingDose = computed(() => doseQuantityOf(lastExisting.value) ?? carrierDose.value);

// PRN and Hold steps carry no administration times, so without a name they read as a step
// whose times were simply left off.
const stepType = (segment) => {
    if (isHeld(segment)) return 'Hold';
    if (isPrn(segment)) return 'PRN';
    return null;
};

const rangeOf = (bounds) => {
    if (!bounds?.start) return '';
    return bounds.end ? `${formatDate(bounds.start)} to ${formatDate(bounds.end)}` : `${formatDate(bounds.start)} onward`;
};

// Every ordered step names its own interval: once steps can slow down, "Step 2 · 1 Tablet ·
// 08:00" no longer says whether that is daily or weekly.
const describeExisting = (segment, index) => {
    const dose = doseQuantityOf(segment);
    const repeat = segment?.timing?.repeat;
    const times = (repeat?.timeOfDay || []).map((time) => formatClockTime(time)).join(', ');
    const interval = stepType(segment) ? null : labelForInterval(windowFromSegment(segment));
    const span = rangeOf(repeat?.boundsPeriod);
    return [`Step ${index + 1}`, dose?.value !== undefined ? `${dose.value} ${dose.unit || ''}`.trim() : null, stepType(segment), times, interval, span].filter(Boolean).join(' · ');
};

const period = computed(() => (Number(props.orderPeriod) > 0 ? Number(props.orderPeriod) : 1));

const periodUnit = computed(() => props.orderPeriodUnit || 'd');

const orderIntervalLabel = computed(() => labelForInterval({ period: period.value, periodUnit: periodUnit.value }));

// A step states its own interval, so the mismatch message names that one rather than the order's.
const intervalLabelOf = (index) => labelForInterval(steps.value[index]);

const lastIndex = computed(() => steps.value.length - 1);

const isFinalStep = (index) => index === lastIndex.value;

const isUnbounded = (index) => isFinalStep(index) && afterFinal.value === 'continue';

// The step the order itself describes. Its dose and its interval both come from the order,
// which is what keeps the sig an honest statement of how the medication starts.
const isOrderStep = (index) => !isAppend.value && index === 0;

const durationOf = (step, index) => {
    if (isUnbounded(index)) return undefined;
    const value = Number(step?.durationValue);
    if (!Number.isFinite(value) || value <= 0) return undefined;
    return { value, unit: unitName(step.durationUnit, value), system: 'http://unitsofmeasure.org', code: step.durationUnit };
};

const doseOf = (step, index) => {
    // Step 1 of a new taper inherits the medication's dose; an appended step never does,
    // because the dose it descends from is on the record, not on this form.
    if (isOrderStep(index)) return undefined;
    const value = Number(step?.doseValue);
    // Zero is finite, and a zero-dose step reaches the chart as a due dose of nothing. A step
    // that gives nothing is a hold, which states a reason.
    if (!Number.isFinite(value) || value <= 0) return undefined;
    const quantity = { value };
    if (carrierDose.value?.unit) quantity.unit = carrierDose.value.unit;
    if (carrierDose.value?.system) quantity.system = carrierDose.value.system;
    if (carrierDose.value?.code) quantity.code = carrierDose.value.code;
    return quantity;
};

const holdReasonConceptFor = (step) => holdReasonConceptOf(holdReasonConcepts.value, step?.holdReasonCode);

const holdReasonItems = computed(() => itemsForHoldReasons(holdReasonConcepts.value));

// The resolver owns every start. Re-chaining here rather than shifting dates by hand is
// what keeps a gap or an overlap unrepresentable.
const chainedShells = computed(() => {
    if (!startDate.value) return [];
    try {
        const shells = steps.value.map((step, index) => ({
            stepDuration: durationOf(step, index),
            timing: { repeat: windowRepeat(step) },
        }));
        return rechainTaper(shells, startDate.value);
    } catch {
        return [];
    }
});

const boundsOf = (index) => chainedShells.value[index]?.timing?.repeat?.boundsPeriod || null;

// A PRN step states no times and no days, so the stretch it covers is the only thing on its
// card that says when it runs. Every step states both ends of it.
const chainedRanges = computed(() => chainedShells.value.map((dosage) => rangeOf(dosage.timing?.repeat?.boundsPeriod)));

// A step whose 'Continue for' is shorter than its own interval — 3 days on an every-week
// step — occupies its stretch of the chain and administers nothing inside it. Its own
// interval, not the order's: that is the trap a time-based taper walks into.
const emptySteps = computed(() =>
    chainedShells.value
        .map((shell, index) => index)
        .filter((index) => {
            const step = steps.value[index];
            if (!step || step.type !== 'scheduled') return false;
            if (!filledTimes(step).length) return false;
            if (usesDayOfWeek(step) && !step.dayOfWeek.length) return false;
            return nextOccurrences(chainedShells.value[index], { limit: 1 }).length === 0;
        }),
);

const stepLabel = (index) => existingSteps.value.length + index + 1;

const emptyStepLabels = computed(() => emptySteps.value.map(stepLabel).join(', '));

const doseValueOf = (step, index) => Number(isOrderStep(index) ? carrierDose.value?.value : step?.doseValue);

/**
 * How much drug the step gives per day. The one number that compares two steps whatever
 * they vary — dose, times of day, days of week, or the interval — so a step that halves the
 * dose while doubling the frequency reads as the flat step it is.
 */
const rateOf = (index) => {
    const step = steps.value[index];
    if (!step || step.type !== 'scheduled') return null;
    const value = doseValueOf(step, index);
    if (!Number.isFinite(value)) return null;
    return dosesPerDay({ doseAndRate: [{ dose: { Quantity: { value } } }], timing: { repeat: windowRepeat(step) } });
};

const previousRateOf = (index) => {
    if (index > 0) return rateOf(index - 1);
    // Step 1 of a new taper descends from the order itself, which the form has not asked to
    // vary — there is nothing before it to compare against.
    if (!isAppend.value) return null;
    return dosesPerDay(lastExisting.value, carrierDose.value);
};

const previousDoseValueOf = (index) => (index > 0 ? doseValueOf(steps.value[index - 1], index - 1) : Number(lastExistingDose.value?.value));

// A taper descends. A step that raises the daily dose is a titration, which the schedule
// would execute on its date with no clinician in the loop.
const titrationSteps = computed(() =>
    steps.value
        .map((step, index) => index)
        .filter((index) => {
            const previous = previousRateOf(index);
            const current = rateOf(index);
            return previous !== null && current !== null && current > previous;
        }),
);

const isTitrationStep = (index) => titrationSteps.value.includes(index);

const isTitrationAcknowledged = (index) => titrationAcknowledgedSteps.value.has(index);

const setTitrationAcknowledged = (index, acknowledged) => {
    const next = new Set(titrationAcknowledgedSteps.value);
    if (acknowledged) next.add(index);
    else next.delete(index);
    titrationAcknowledgedSteps.value = next;
};

// Moving the dose and the frequency in opposite directions by the same factor changes the
// regimen and not the exposure. It is neither a taper step nor a titration, and it is
// usually a slip rather than an intent.
const flatSteps = computed(() =>
    steps.value
        .map((step, index) => index)
        .filter((index) => {
            const previous = previousRateOf(index);
            const current = rateOf(index);
            if (previous === null || current === null || Math.abs(current - previous) > 1e-9) return false;
            return doseValueOf(steps.value[index], index) !== previousDoseValueOf(index);
        }),
);

const flatStepLabels = computed(() => flatSteps.value.map(stepLabel).join(', '));

const perIntervalOf = (index) => windowPerInterval(steps.value[index]);

// A later step states its own frequency, so the count it has to match is its own rather than
// the order's — the same rule the schedule form applies against the order.
const stepFrequencyMismatch = (index) => !isOrderStep(index) && windowFrequencyMismatch(steps.value[index]);

const mismatchedSteps = computed(() => steps.value.map((step, index) => index).filter(stepFrequencyMismatch));

// Mirrors what buildSegment and rechainTaper throw on, so neither throw is reachable through the form.
const stepOneFrequencyMismatch = computed(() => {
    const step = steps.value[0];
    if (isAppend.value || !props.orderFrequency || !step || step.type !== 'scheduled') return false;
    return perIntervalOf(0) !== props.orderFrequency;
});

// D14: no schedule describes a window that has already passed. An appended step takes over
// from a running final step, and a new taper places every step from its own start — neither
// can begin before today.
const startsInPast = computed(() => Boolean(startDate.value && startDate.value < today.value));

const canSave = computed(() => {
    if (props.readOnly) return false;
    if (!hasStructuredDose.value) return false;
    if (stepsWithUnreadableTime.value.size) return false;
    if (startsInPast.value) return false;
    if (stepOneFrequencyMismatch.value) return false;
    if (mismatchedSteps.value.length) return false;
    if (!startDate.value || !steps.value.length) return false;
    if (titrationSteps.value.some((index) => !isTitrationAcknowledged(index))) return false;
    if (emptySteps.value.length) return false;
    return steps.value.every((step, index) => {
        if (!isUnbounded(index) && !(Number(step.durationValue) > 0)) return false;
        if (!isOrderStep(index) && step.type === 'scheduled' && !(Number(step.doseValue) > 0)) return false;
        return isWindowComplete(step);
    });
});

// Steps are removable from the middle, so each card is keyed by identity rather than position:
// keyed by index, the pickers of a later step render on the card before it and an unreadable
// time is flagged against a picker that no longer shows it.
let lastStepKey = 0;

const stepShell = (window, overrides = {}) => ({
    ...window,
    doseValue: carrierDose.value?.value ?? null,
    durationValue: null,
    durationUnit: 'd',
    ...overrides,
    key: ++lastStepKey,
});

const makeStep = (previous) => {
    if (previous) {
        return stepShell(
            { ...previous, times: [...previous.times], dayOfWeek: [...previous.dayOfWeek] },
            {
                doseValue: previous.doseValue,
                // A hold is a decision about one moment and states its reason fresh, so a step
                // seeded from one continues its schedule rather than its pause.
                type: previous.type === 'hold' ? 'scheduled' : previous.type,
                holdReasonCode: null,
                holdNotes: '',
            },
        );
    }
    const fresh = blankWindow({ period: period.value, periodUnit: periodUnit.value });
    fresh.administrationWindow = accountAdministrationWindow(authStore.account);
    const frequency = Number(props.orderFrequency) > 0 ? Number(props.orderFrequency) : 1;
    return stepShell({ ...fresh, frequency, times: grownTimes({ ...fresh, times: [] }, props.orderFrequency) });
};

/** A new step shaped like the last saved one — the shape a taper continues from. */
const stepFromSegment = (segment) => {
    const seeded = windowFromSegment(segment);
    return stepShell(seeded, {
        doseValue: doseQuantityOf(segment)?.value ?? null,
        type: seeded.type === 'hold' ? 'scheduled' : seeded.type,
        holdReasonCode: null,
        holdNotes: '',
    });
};

/** A step of an unsaved taper, restored as it was entered. */
const draftStepFromSegment = (segment) => {
    const seeded = windowFromSegment(segment);
    return stepShell(seeded, {
        doseValue: doseQuantityOf(segment)?.value ?? null,
        durationValue: segment?.stepDuration?.value ?? null,
        durationUnit: segment?.stepDuration?.code || 'd',
    });
};

const reset = () => {
    const draft = draftSteps.value;
    if (isAppend.value) {
        startDate.value = appendStart.value && !appendStartInPast.value ? appendStart.value : today.value;
        steps.value = [stepFromSegment(lastExisting.value)];
        afterFinal.value = 'stop';
    } else if (draft.length) {
        startDate.value = segmentStartDay(draft[0]) || today.value;
        steps.value = draft.map(draftStepFromSegment);
        // The final step either states how long it runs or runs until cancelled, which is
        // exactly whether it was given a duration.
        afterFinal.value = draft[draft.length - 1]?.stepDuration ? 'stop' : 'continue';
    } else {
        startDate.value = today.value;
        const first = makeStep(null);
        steps.value = [first, makeStep(first)];
        afterFinal.value = 'stop';
    }
    titrationAcknowledgedSteps.value = new Set();
    stepsWithUnreadableTime.value = new Set();
    taperNotes.value = props.medication?.taperNotes || '';
    // Appending carries on where the taper already runs.
    site.value = (existingSteps.value[existingSteps.value.length - 1]?.administeringLocation?.id ?? patientSite.value) || null;
};

watch(
    () => props.modelValue,
    (open) => {
        if (open) reset();
    },
    { immediate: true },
);

// The enrollments may still be reading when the dialog opens, so the default lands on whichever
// resolves second. Never over an answer already there, and never when appending, where the site
// carries on from the taper already running.
watch(patientSite, (id) => {
    if (props.modelValue && !isAppend.value && id && !site.value) site.value = id;
});

// An acknowledgement covers the ascending step the user was shown, not a standing waiver:
// a step that stops ascending drops its own, and does not carry it if it ascends again.
watch(titrationSteps, (ascending) => {
    const kept = new Set([...titrationAcknowledgedSteps.value].filter((index) => ascending.includes(index)));
    if (kept.size !== titrationAcknowledgedSteps.value.size) titrationAcknowledgedSteps.value = kept;
});

const patchStep = (index, patch) => {
    steps.value = steps.value.map((step, position) => (position === index ? { ...step, ...patch } : step));
};

const addStep = () => {
    steps.value = [...steps.value, makeStep(steps.value[steps.value.length - 1])];
};

/**
 * Drop a step and shift the index-keyed sets down with it.
 *
 * Both sets are positional, so removing a step moves every later one onto a different position. Clearing only the removed index leaves a flag stranded on a
 * step that no longer exists - `canSave` reads `stepsWithUnreadableTime.size` first, so Save
 * stays dead - and moves an acknowledgement onto a step nobody acknowledged.
 */
const reindexAfterRemoval = (set, index) => {
    const next = new Set();
    for (const position of set) {
        if (position === index) continue;
        next.add(position > index ? position - 1 : position);
    }
    return next;
};

const removeStep = (index) => {
    steps.value = steps.value.filter((step, position) => position !== index);
    stepsWithUnreadableTime.value = reindexAfterRemoval(stepsWithUnreadableTime.value, index);
    titrationAcknowledgedSteps.value = reindexAfterRemoval(titrationAcknowledgedSteps.value, index);
};

const setStepTimeInvalid = (index, isInvalid) => {
    const next = new Set(stepsWithUnreadableTime.value);
    if (isInvalid) next.add(index);
    else next.delete(index);
    stepsWithUnreadableTime.value = next;
};

const close = () => {
    emit('update:modelValue', false);
};

const save = () => {
    const practitioner = authStore.practitioner;
    const addedBy = practitioner?.id ? { id: practitioner.id, resourceType: practitioner.resourceType || 'Practitioner' } : undefined;

    const built = steps.value.map((step, index) =>
        buildSegment({
            medication: props.medication,
            // Sequence decides whether buildSegment honours the step's own dose, so an
            // appended step has to count from the end of what is already there.
            sequence: existingSteps.value.length + index + 1,
            ...windowSegmentInput(step, { holdReason: holdReasonConceptFor(step) }),
            // A placeholder: rechainTaper derives every start from the taper start and the
            // preceding durations, and overwrites this one.
            start: startDate.value,
            dose: doseOf(step, index),
            stepDuration: durationOf(step, index),
            addedBy,
            administeringLocation: site.value ? { id: site.value, resourceType: 'Location' } : undefined,
        }),
    );

    emit('saved', rechainTaper(built, startDate.value), { taperNotes: taperNotes.value.trim() || undefined });
    close();
};
</script>

<template>
    <v-dialog v-model="isOpen" scrollable persistent :fullscreen="vuetifyDisplay.xs.value" max-width="600px">
        <v-card data-cy="medicationTaperDialog" class="d-flex flex-column">
            <v-toolbar density="compact" flat class="flex-grow-0">
                <v-toolbar-title>{{ isAppend ? 'Add taper step' : 'Add taper' }}</v-toolbar-title>
                <BnToolbarBtn type="secondary" label="Cancel" icon="times" data-cy="cancelTaperBtn" @click="close" />
                <BnToolbarBtn v-if="!readOnly" type="primary" right label="Save" icon="check" :disabled="!canSave" data-cy="saveTaperBtn" @click="save" />
            </v-toolbar>
            <v-divider></v-divider>
            <v-card-text class="pa-4 flex-grow-1 overflow-auto">
                <div v-if="medicationName" class="mb-4" data-cy="taperMedicationReference">
                    <div class="text-body-2 font-weight-bold" data-cy="taperMedicationName">{{ medicationName }}</div>
                    <div v-if="sigLine" class="text-caption text-medium-emphasis" data-cy="taperSigLine">{{ sigLine }}</div>
                </div>

                <v-alert v-if="!hasStructuredDose" type="warning" variant="tonal" density="compact" class="mb-4" data-cy="taperRequiresStructuredDose">
                    This medication's dose is free text, so it cannot be tapered. Every step of a taper is measured against the medication's dose, and free text cannot be compared. Discontinue this
                    medication and add it again with a dose amount and unit, then build the taper.
                </v-alert>

                <template v-if="isAppend">
                    <div class="text-caption text-medium-emphasis">Steps already ordered</div>
                    <v-list density="compact" class="py-0 mb-3" data-cy="existingTaperSteps">
                        <v-list-item v-for="(segment, index) in existingSteps" :key="index" class="px-2" :data-cy="`existingTaperStep${index}`">
                            <v-list-item-title class="text-body-2">{{ describeExisting(segment, index) }}</v-list-item-title>
                        </v-list-item>
                    </v-list>
                    <v-alert type="info" variant="tonal" density="compact" class="mb-4" data-cy="taperAppendOnlyAlert">
                        Steps already ordered cannot be changed here. Add the next step below.
                    </v-alert>
                </template>

                <div class="d-flex flex-wrap align-start ga-3">
                    <div style="width: 190px">
                        <BnDatePicker
                            v-model="startDate"
                            :disabled="readOnly || (isAppend && Boolean(appendStart) && !appendStartInPast)"
                            :strict-date="false"
                            :label="isAppend ? 'New step starts' : 'Taper start date'"
                            data-cy="taperStartDate"
                        ></BnDatePicker>
                    </div>

                    <!-- Stamped onto every step this writes. On the schedule rather than the order,
                         so a later step can be moved without the order being rewritten. -->
                    <v-select
                        v-if="sites.length"
                        v-model="site"
                        :items="sites"
                        :disabled="readOnly"
                        item-title="label"
                        item-value="id"
                        clearable
                        :label="siteLabel"
                        hide-details
                        style="width: 260px"
                        data-cy="taperAdministrationSite"
                    ></v-select>
                </div>
                <div v-if="isAppend && appendStart && !appendStartInPast" class="text-caption text-medium-emphasis mb-2" data-cy="taperAppendStartMessage">
                    The day after the current final step ends.
                </div>
                <div v-if="isAppend && appendStartInPast" class="text-caption text-medium-emphasis mb-2" data-cy="taperAppendFinishedMessage">
                    The taper has already finished, so the new step starts on the day you name.
                </div>
                <div v-if="isAppend && !appendStart" class="text-caption text-medium-emphasis mb-2" data-cy="taperAppendClosesFinalMessage">
                    The final step runs until cancelled, so this date is the day the new step takes over from it.
                </div>
                <div v-if="startsInPast" class="text-caption text-warning mb-2" data-cy="taperStartsInPastMessage">
                    {{ isAppend ? 'A new step starts today or later.' : 'A taper starts today or later.' }}
                </div>

                <v-card v-for="(step, index) in steps" :key="step.key" variant="outlined" class="mt-3 pa-3" :data-cy="`taperStep${index}`">
                    <div class="d-flex align-center mb-1">
                        <div class="text-subtitle-2">Step {{ stepLabel(index) }}{{ isOrderStep(index) ? ' - original order' : '' }}</div>
                        <div v-if="chainedRanges[index]" class="text-caption text-medium-emphasis ml-2" :data-cy="`taperStepRange${index}`">{{ chainedRanges[index] }}</div>
                        <v-spacer></v-spacer>
                        <v-btn
                            v-if="index > 0 && !readOnly"
                            variant="text"
                            size="small"
                            color="secondary"
                            :aria-label="`Remove step ${stepLabel(index)}`"
                            :data-cy="`removeTaperStep${index}`"
                            @click="removeStep(index)"
                        >
                            <FontAwesomeIcon :icon="['far', 'trash']" />
                        </v-btn>
                    </div>

                    <div class="d-flex flex-wrap">
                        <v-text-field
                            v-if="isOrderStep(index)"
                            :model-value="doseDisplay"
                            readonly
                            label="Dose"
                            class="mr-2 flex-grow-0 flex-shrink-0"
                            style="width: 170px"
                            :data-cy="`taperStepDoseDisplay${index}`"
                        ></v-text-field>
                        <v-combobox
                            v-else
                            :model-value="step.doseValue"
                            :items="doseAmountOptions"
                            :disabled="readOnly"
                            label="Dose"
                            :suffix="carrierDose?.unit"
                            class="mr-2 flex-grow-0 flex-shrink-0"
                            style="width: 170px"
                            :data-cy="`taperStepDose${index}`"
                            @update:model-value="(value) => patchStep(index, { doseValue: value })"
                        ></v-combobox>
                        <v-select
                            :model-value="step.type"
                            :items="typeOptions"
                            :disabled="readOnly"
                            label="Administration type"
                            class="flex-grow-1"
                            style="min-width: 200px"
                            :data-cy="`taperStep${index}Type`"
                            @update:model-value="(value) => patchStep(index, { type: value })"
                        ></v-select>
                    </div>

                    <BnMedicationWindowFields
                        :model-value="step"
                        :data-cy="`taperStep${index}`"
                        :read-only="readOnly"
                        show-recurrence
                        :interval-editable="!isOrderStep(index)"
                        :frequency-editable="!isOrderStep(index)"
                        :order-frequency="isOrderStep(index) ? orderFrequency : null"
                        :hold-reasons="holdReasonItems"
                        :preview-from="boundsOf(index)?.start || null"
                        :preview-end="boundsOf(index)?.end || null"
                        @patch="(apply) => patchStep(index, apply(steps[index]))"
                        @update:invalid="(value) => setStepTimeInvalid(index, value)"
                    >
                        <template #bounds>
                            <div class="d-flex flex-wrap">
                                <v-combobox
                                    :model-value="step.durationValue"
                                    :items="stepDurationOptions"
                                    :disabled="readOnly || isUnbounded(index)"
                                    label="Continue for"
                                    class="mr-2 flex-grow-0 flex-shrink-0"
                                    style="width: 150px"
                                    :data-cy="`taperStepDuration${index}`"
                                    @update:model-value="(value) => patchStep(index, { durationValue: value })"
                                ></v-combobox>
                                <v-select
                                    :model-value="step.durationUnit"
                                    :items="durationUnitOptions"
                                    :disabled="readOnly || isUnbounded(index)"
                                    label="Unit"
                                    class="flex-grow-0"
                                    style="width: 160px"
                                    :data-cy="`taperStepDurationUnit${index}`"
                                    @update:model-value="(value) => patchStep(index, { durationUnit: value })"
                                ></v-select>
                            </div>
                        </template>
                        <template #scheduled-messages>
                            <v-alert
                                v-if="stepFrequencyMismatch(index)"
                                type="error"
                                variant="tonal"
                                density="compact"
                                class="mb-3 text-caption"
                                :data-cy="`taperStep${index}FrequencyMismatchMessage`"
                            >
                                This step is {{ step.frequency }} per {{ intervalLabelOf(index).replace('every ', '') }}, and its times give {{ perIntervalOf(index) }}. Match the frequency to save.
                            </v-alert>
                            <v-alert
                                v-if="isOrderStep(index) && stepOneFrequencyMismatch"
                                type="error"
                                variant="tonal"
                                density="compact"
                                class="mb-3 text-caption"
                                data-cy="stepOneFrequencyMismatchMessage"
                            >
                                The order is {{ orderFrequency }} per {{ orderIntervalLabel.replace('every ', '') }}, and this step gives {{ perIntervalOf(index) }}. Match the order to save.
                            </v-alert>
                        </template>
                    </BnMedicationWindowFields>

                    <template v-if="isTitrationStep(index)">
                        <v-alert type="warning" variant="tonal" density="compact" class="mt-2" :data-cy="`taperStep${index}TitrationWarning`">
                            This step raises the total daily dose — by the dose itself, by giving it more often, or by both. Every step of a taper runs automatically on its date with nobody deciding,
                            so this increase would happen without a clinician reviewing the patient first. Record a conditional titration in the medication's administration instructions instead, and
                            add each increase as its own segment once it is ordered.
                        </v-alert>
                        <v-checkbox
                            :model-value="isTitrationAcknowledged(index)"
                            :disabled="readOnly"
                            label="I understand this increase will run automatically"
                            density="comfortable"
                            hide-details
                            :data-cy="`taperStep${index}TitrationAcknowledge`"
                            @update:model-value="(value) => setTitrationAcknowledged(index, value === true)"
                        ></v-checkbox>
                    </template>
                </v-card>

                <v-btn v-if="!readOnly" variant="text" color="primary" class="mt-2" data-cy="addTaperStepBtn" @click="addStep">
                    <FontAwesomeIcon :icon="['far', 'plus']" class="mr-1" />
                    Add step
                </v-btn>

                <v-radio-group v-model="afterFinal" :disabled="readOnly" class="mt-2" hide-details data-cy="taperAfterFinalRadioGroup">
                    <template #label>
                        <span class="text-caption text-medium-emphasis">After final step</span>
                    </template>
                    <v-radio label="Stop medication" value="stop" data-cy="taperAfterFinalStop"></v-radio>
                    <v-radio label="Continue final dose until cancelled" value="continue" data-cy="taperAfterFinalContinue"></v-radio>
                </v-radio-group>

                <v-alert v-if="emptySteps.length" type="warning" variant="tonal" density="compact" class="mt-4" data-cy="emptyStepWarning">
                    {{ emptySteps.length > 1 ? 'Steps' : 'Step' }} {{ emptyStepLabels }} {{ emptySteps.length > 1 ? 'are' : 'is' }} shorter than
                    {{ emptySteps.length > 1 ? 'their own intervals' : 'its own interval' }}, so {{ emptySteps.length > 1 ? 'they administer' : 'it administers' }} nothing. Lengthen the step or
                    shorten its interval.
                </v-alert>

                <v-alert v-if="flatSteps.length" type="info" variant="tonal" density="compact" class="mt-4" data-cy="flatStepWarning">
                    {{ flatSteps.length > 1 ? 'Steps' : 'Step' }} {{ flatStepLabels }} {{ flatSteps.length > 1 ? 'change' : 'changes' }} the dose and how often it is given by the same factor, so the
                    total daily dose is unchanged. Check this is the regimen change you meant rather than a step of the taper.
                </v-alert>

                <v-textarea v-model="taperNotes" :disabled="readOnly" label="Taper notes" rows="2" auto-grow class="mt-4" data-cy="taperNotesField"></v-textarea>

                <DataProvider v-model="holdReasonConcepts" collection :query="HOLD_REASON_QUERY" />
            </v-card-text>
        </v-card>
    </v-dialog>
</template>
